import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Volume2, VolumeX, Play, Pause, SkipForward, SkipBack, Loader2, ArrowLeft, ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const audioCache = new Map();

const base64ToBlob = (base64, contentType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

// Image Slideshow Component
const ImageSlideshow = ({ slides, currentTime, duration, isPlaying }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  // Auto-advance slides based on audio progress
  useEffect(() => {
    if (!slides || slides.length === 0 || duration <= 0) return;
    const segmentDuration = duration / slides.length;
    const newIndex = Math.min(Math.floor(currentTime / segmentDuration), slides.length - 1);
    if (newIndex !== activeIndex) {
      setTransitioning(true);
      setTimeout(() => {
        setActiveIndex(newIndex);
        setTransitioning(false);
      }, 200);
    }
  }, [currentTime, duration, slides, activeIndex]);

  useEffect(() => {
    setActiveIndex(0);
  }, [slides]);

  if (!slides || slides.length === 0) return null;

  const goToSlide = (idx) => {
    setTransitioning(true);
    setTimeout(() => {
      setActiveIndex(idx);
      setTransitioning(false);
    }, 200);
  };

  return (
    <div className="mb-5 w-full" data-testid="image-slideshow">
      <div className="relative w-full rounded-xl overflow-hidden bg-slate-900" style={{ minHeight: '280px', maxHeight: '420px' }}>
        <img
          src={`data:image/png;base64,${slides[activeIndex].image_base64}`}
          alt={slides[activeIndex].caption}
          className={`w-full h-auto max-h-[420px] object-contain mx-auto transition-opacity duration-300 ${transitioning ? 'opacity-0' : 'opacity-100'}`}
          data-testid={`slide-image-${activeIndex}`}
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
          <p className="text-white text-sm font-medium text-center" data-testid="slide-caption">
            {slides[activeIndex].caption}
          </p>
        </div>
        {activeIndex > 0 && (
          <button
            onClick={() => goToSlide(activeIndex - 1)}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            data-testid="slide-prev-btn"
          >
            <ChevronLeft size={24} />
          </button>
        )}
        {activeIndex < slides.length - 1 && (
          <button
            onClick={() => goToSlide(activeIndex + 1)}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            data-testid="slide-next-btn"
          >
            <ChevronRight size={24} />
          </button>
        )}
        <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2.5 py-1 rounded-full font-medium">
          {activeIndex + 1} / {slides.length}
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-3">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              idx === activeIndex ? 'w-7 bg-teal-500' : 'w-2.5 bg-teal-200 hover:bg-teal-300'
            }`}
            data-testid={`slide-dot-${idx}`}
          />
        ))}
      </div>
    </div>
  );
};

// Image Loading Skeleton
const ImageSkeleton = () => (
  <div className="mb-5 w-full" data-testid="image-skeleton">
    <div className="relative w-full rounded-xl overflow-hidden bg-gradient-to-br from-teal-50 to-slate-100 flex flex-col items-center justify-center" style={{ minHeight: '280px' }}>
      <div className="animate-pulse flex flex-col items-center gap-3">
        <ImageIcon size={48} className="text-teal-300" />
        <div className="flex items-center gap-2">
          <Loader2 size={16} className="animate-spin text-teal-500" />
          <span className="text-sm text-teal-600 font-medium">Generating illustrations...</span>
        </div>
        <p className="text-xs text-slate-400 mt-1">AI is creating visual aids for this topic (may take up to a minute)</p>
      </div>
    </div>
  </div>
);

// Image Error / Retry Component
const ImageError = ({ onRetry }) => (
  <div className="mb-5 w-full" data-testid="image-error">
    <div className="relative w-full rounded-xl overflow-hidden bg-gradient-to-br from-red-50 to-slate-50 border border-red-100 flex flex-col items-center justify-center py-10">
      <ImageIcon size={40} className="text-red-300 mb-3" />
      <p className="text-sm text-red-600 font-medium mb-1">Could not load illustrations</p>
      <p className="text-xs text-slate-400 mb-4">Image generation timed out or failed</p>
      <Button onClick={onRetry} size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="retry-images-btn">
        Try Again
      </Button>
    </div>
  </div>
);

const AudioLearning = ({ onBack }) => {
  const { user } = useAuth();
  const [selectedSubject, setSelectedSubject] = useState('Mathematics');
  const [selectedClass, setSelectedClass] = useState('6');
  const [syllabus, setSyllabus] = useState(null);
  const [loading, setLoading] = useState(false);

  const [language, setLanguage] = useState('english');
  const [pace, setPace] = useState('normal');
  const [voice, setVoice] = useState('nova');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(true);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Image slideshow state
  const [slides, setSlides] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imagesCacheRef = useRef(new Map());

  const audioRef = useRef(null);
  const lastPlaybackTimeRef = useRef(0);
  const currentChapterIndexRef = useRef(0);
  const currentTopicIndexRef = useRef(0);

  useEffect(() => {
    loadSyllabus();
  }, [selectedClass, selectedSubject]);

  useEffect(() => {
    return () => {
      clearAudio();
      audioCache.forEach((url) => URL.revokeObjectURL(url));
      audioCache.clear();
    };
  }, []);

  // Load images when topic changes
  useEffect(() => {
    currentChapterIndexRef.current = currentChapterIndex;
    currentTopicIndexRef.current = currentTopicIndex;
    if (syllabus) {
      loadTopicImages(currentChapterIndex, currentTopicIndex);
    }
  }, [currentChapterIndex, currentTopicIndex, syllabus, selectedSubject, selectedClass]);

  const loadSyllabus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/student/syllabus`, {
        params: { class_level: selectedClass, subject: selectedSubject }
      });
      setSyllabus(response.data);
    } catch (error) {
      console.error('Error loading syllabus:', error);
      toast.error('Failed to load syllabus');
    } finally {
      setLoading(false);
    }
  };

  const loadTopicImages = async (chapterIdx, topicIdx) => {
    if (!syllabus) return;
    const chapter = syllabus.chapters[chapterIdx];
    const topic = chapter?.topics[topicIdx];
    if (!chapter || !topic) return;

    const cacheKey = `${selectedSubject}_${selectedClass}_${chapter.name}_${topic.name}`;

    // Check local cache
    if (imagesCacheRef.current.has(cacheKey)) {
      setSlides(imagesCacheRef.current.get(cacheKey));
      setImageError(false);
      return;
    }

    setIsLoadingImages(true);
    setSlides([]);
    setImageError(false);

    try {
      // Kick off generation (returns immediately)
      const response = await axios.post(`${API}/student/audio/topic-images`, {
        chapter_name: chapter.name,
        topic_name: topic.name,
        class_level: selectedClass,
        subject: selectedSubject
      });

      if (response.data.status === 'ready' && response.data.slides?.length > 0) {
        // Already cached - use immediately
        imagesCacheRef.current.set(cacheKey, response.data.slides);
        setSlides(response.data.slides);
        setIsLoadingImages(false);
        return;
      }

      // Poll for completion
      const pollForImages = async () => {
        const maxPolls = 40; // 40 x 5s = 200s max wait
        for (let i = 0; i < maxPolls; i++) {
          await new Promise(r => setTimeout(r, 5000));

          // Check if topic changed while polling
          if (currentChapterIndexRef.current !== chapterIdx || currentTopicIndexRef.current !== topicIdx) {
            return; // Stop polling, topic changed
          }

          try {
            const statusRes = await axios.get(`${API}/student/audio/topic-images/status`, {
              params: {
                subject: selectedSubject,
                class_level: selectedClass,
                chapter_name: chapter.name,
                topic_name: topic.name
              }
            });

            if (statusRes.data.status === 'ready' && statusRes.data.slides?.length > 0) {
              imagesCacheRef.current.set(cacheKey, statusRes.data.slides);
              setSlides(statusRes.data.slides);
              setIsLoadingImages(false);
              return;
            } else if (statusRes.data.status === 'failed') {
              setImageError(true);
              setIsLoadingImages(false);
              return;
            }
            // Still generating, continue polling
          } catch {
            // Network error during poll, continue
          }
        }
        // Timed out
        setImageError(true);
        setIsLoadingImages(false);
      };

      pollForImages();

    } catch (error) {
      console.error('Image generation error:', error);
      setImageError(true);
      setIsLoadingImages(false);
    }
  };

  const getCacheKey = (text, lang, p, v) => `${text}_${lang}_${p}_${v}`;

  const clearAudio = (savePosition = false) => {
    if (audioRef.current) {
      if (savePosition && audioRef.current.currentTime > 0) {
        lastPlaybackTimeRef.current = audioRef.current.currentTime;
      }
      audioRef.current.onended = null;
      audioRef.current.ontimeupdate = null;
      audioRef.current.onerror = null;
      audioRef.current.onloadedmetadata = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  const buildTextForTopic = (chapterIdx, topicIdx) => {
    if (!syllabus) return null;
    const chapter = syllabus.chapters[chapterIdx];
    const topic = chapter?.topics[topicIdx];
    if (!chapter || !topic) return null;
    return {
      chapter_name: chapter.name,
      topic_name: topic.name,
      class_level: selectedClass,
      subject: selectedSubject
    };
  };

  const playAudioForTopic = async (chapterIdx, topicIdx, autoAdvanceOnEnd = false, resumeFromTime = 0) => {
    if (!audioEnabled || !syllabus) return;

    const topicInfo = buildTextForTopic(chapterIdx, topicIdx);
    if (!topicInfo) return;

    const cacheKey = `${topicInfo.subject}_${topicInfo.class_level}_${topicInfo.chapter_name}_${topicInfo.topic_name}_${language}_${pace}_${voice}`;

    setIsGenerating(true);

    try {
      let audioUrl = audioCache.get(cacheKey);

      if (!audioUrl) {
        const response = await axios.post(`${API}/student/audio/explain-topic`, {
          chapter_name: topicInfo.chapter_name,
          topic_name: topicInfo.topic_name,
          class_level: topicInfo.class_level,
          subject: topicInfo.subject,
          language,
          pace,
          voice
        });

        if (response.data.success && response.data.audio_data) {
          const format = response.data.format || 'mp3';
          const mimeType = format === 'wav' ? 'audio/wav' : 'audio/mp3';
          const audioBlob = base64ToBlob(response.data.audio_data, mimeType);
          audioUrl = URL.createObjectURL(audioBlob);
          audioCache.set(cacheKey, audioUrl);
        } else {
          throw new Error('Failed to generate audio explanation');
        }
      }

      clearAudio();

      const newAudio = new Audio(audioUrl);
      audioRef.current = newAudio;

      newAudio.onloadedmetadata = () => {
        setDuration(newAudio.duration);
        if (resumeFromTime > 0 && resumeFromTime < newAudio.duration) {
          newAudio.currentTime = resumeFromTime;
          setCurrentTime(resumeFromTime);
        }
      };

      newAudio.ontimeupdate = () => {
        if (audioRef.current === newAudio) {
          setCurrentTime(newAudio.currentTime);
        }
      };

      newAudio.onended = () => {
        if (audioRef.current === newAudio) {
          setIsPlaying(false);
          setCurrentTime(0);
          lastPlaybackTimeRef.current = 0;
          if (autoAdvanceOnEnd) {
            advanceToNext(chapterIdx, topicIdx, true);
          }
        }
      };

      newAudio.onerror = () => {
        if (audioRef.current === newAudio) {
          setIsPlaying(false);
          toast.error('Audio playback error');
        }
      };

      await newAudio.play();
      setIsPlaying(true);

    } catch (error) {
      console.error('Audio error:', error);
      toast.error('Failed to generate explanation. Please try again.');
      setIsPlaying(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const advanceToNext = (fromChapterIdx, fromTopicIdx, autoPlay = false) => {
    if (!syllabus) return;
    const currentChapter = syllabus.chapters[fromChapterIdx];
    let nextChapterIdx = fromChapterIdx;
    let nextTopicIdx = fromTopicIdx;

    if (fromTopicIdx < currentChapter.topics.length - 1) {
      nextTopicIdx = fromTopicIdx + 1;
    } else if (fromChapterIdx < syllabus.chapters.length - 1) {
      nextChapterIdx = fromChapterIdx + 1;
      nextTopicIdx = 0;
    } else {
      toast.info('You have reached the end of the syllabus');
      return;
    }

    setCurrentChapterIndex(nextChapterIdx);
    setCurrentTopicIndex(nextTopicIdx);
    setCurrentTime(0);

    if (autoPlay) {
      setTimeout(() => {
        playAudioForTopic(nextChapterIdx, nextTopicIdx, true);
      }, 300);
    }
  };

  const handlePlayPause = async () => {
    if (!syllabus) return;
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      await playAudioForTopic(currentChapterIndex, currentTopicIndex, true);
    }
  };

  const handleNext = () => {
    clearAudio();
    setIsPlaying(false);
    setCurrentTime(0);
    advanceToNext(currentChapterIndex, currentTopicIndex, false);
  };

  const handlePrevious = () => {
    if (!syllabus) return;
    clearAudio();
    setIsPlaying(false);
    setCurrentTime(0);
    if (currentTopicIndex > 0) {
      setCurrentTopicIndex(currentTopicIndex - 1);
    } else if (currentChapterIndex > 0) {
      const prevChapter = syllabus.chapters[currentChapterIndex - 1];
      setCurrentChapterIndex(currentChapterIndex - 1);
      setCurrentTopicIndex(prevChapter.topics.length - 1);
    } else {
      toast.info('You are at the beginning of the syllabus');
    }
  };

  const handleChapterClick = (chapterIndex, topicIndex) => {
    clearAudio();
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentChapterIndex(chapterIndex);
    setCurrentTopicIndex(topicIndex);
  };

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    setVoice('nova');
    if (isPlaying) {
      clearAudio(false);
      setIsPlaying(false);
      setCurrentTime(0);
      lastPlaybackTimeRef.current = 0;
      setTimeout(() => {
        playAudioForTopic(currentChapterIndex, currentTopicIndex, true, 0);
      }, 100);
    }
  };

  const handlePaceChange = (newPace) => {
    const savedTime = currentTime;
    setPace(newPace);
    if (isPlaying) {
      clearAudio(true);
      setIsPlaying(false);
      setTimeout(() => {
        playAudioForTopic(currentChapterIndex, currentTopicIndex, true, savedTime);
      }, 100);
    }
  };

  const handleVoiceChange = (newVoice) => {
    const savedTime = currentTime;
    setVoice(newVoice);
    if (isPlaying) {
      clearAudio(true);
      setIsPlaying(false);
      setTimeout(() => {
        playAudioForTopic(currentChapterIndex, currentTopicIndex, true, savedTime);
      }, 100);
    }
  };

  const toggleAudio = () => {
    if (audioEnabled) {
      clearAudio();
      setIsPlaying(false);
    }
    setAudioEnabled(!audioEnabled);
  };

  const currentChapter = syllabus?.chapters[currentChapterIndex];
  const currentTopic = currentChapter?.topics[currentTopicIndex];
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-800 text-white px-6 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-teal-600 rounded-lg transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft size={24} />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold" data-testid="audio-learning-title">
                Audio Learning
              </h1>
              <p className="text-teal-200 text-sm">Listen & learn with AI-powered audio and illustrations</p>
            </div>
          </div>
          <Badge className="bg-white/20 text-white border-white/30">Class {selectedClass}</Badge>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Controls Card */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6 border border-teal-100">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger data-testid="class-select" className="border-teal-200 focus:ring-teal-500">
                <SelectValue placeholder="Select Class" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">Class 6</SelectItem>
                <SelectItem value="7">Class 7</SelectItem>
                <SelectItem value="8">Class 8</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
              <SelectTrigger data-testid="subject-select" className="border-teal-200 focus:ring-teal-500">
                <SelectValue placeholder="Select Subject" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Mathematics">Mathematics</SelectItem>
                <SelectItem value="Science">Science</SelectItem>
                <SelectItem value="English">English</SelectItem>
              </SelectContent>
            </Select>

            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger data-testid="language-select" className="border-teal-200 focus:ring-teal-500">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
              </SelectContent>
            </Select>

            <Select value={pace} onValueChange={handlePaceChange}>
              <SelectTrigger data-testid="pace-select" className="border-teal-200 focus:ring-teal-500">
                <SelectValue placeholder="Pace" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="slow">Slow Pace</SelectItem>
                <SelectItem value="normal">Normal Pace</SelectItem>
                <SelectItem value="fast">Fast Pace</SelectItem>
              </SelectContent>
            </Select>

            <Select value={voice} onValueChange={handleVoiceChange}>
              <SelectTrigger data-testid="voice-select" className="border-teal-200 focus:ring-teal-500">
                <SelectValue placeholder="Voice" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nova">Nova (Energetic)</SelectItem>
                <SelectItem value="alloy">Alloy (Neutral)</SelectItem>
                <SelectItem value="echo">Echo (Calm)</SelectItem>
                <SelectItem value="fable">Fable (Story)</SelectItem>
                <SelectItem value="shimmer">Shimmer (Cheerful)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Current Topic Display with Images */}
        {currentChapter && currentTopic && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6 border border-teal-100">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-5">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold mb-2" data-testid="current-chapter">
                    Chapter {currentChapter.order}: {currentChapter.name}
                  </h2>
                  <Badge className="bg-white/20 text-white border-white/30">{currentChapter.term}</Badge>
                </div>
                <Button
                  onClick={toggleAudio}
                  variant={audioEnabled ? "default" : "outline"}
                  size="sm"
                  className={audioEnabled ? "bg-white text-teal-700 hover:bg-white/90" : "border-white text-white hover:bg-white/20"}
                  data-testid="toggle-audio-btn"
                >
                  {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="p-5">
              <div className="bg-teal-50 p-5 rounded-xl mb-4 border border-teal-100">
                <h3 className="text-lg font-semibold mb-2 text-teal-800" data-testid="current-topic">
                  Topic: {currentTopic.name}
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <Badge className={currentTopic.status === 'covered' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                    {currentTopic.status === 'covered' ? 'Covered' : 'Not Covered'}
                  </Badge>
                  {slides.length > 0 && (
                    <Badge className="bg-teal-100 text-teal-700">
                      <ImageIcon size={12} className="mr-1 inline" /> {slides.length} Illustrations
                    </Badge>
                  )}
                </div>
              </div>

              {/* AI-Generated Image Slideshow */}
              {isLoadingImages ? (
                <ImageSkeleton />
              ) : slides.length > 0 ? (
                <ImageSlideshow
                  slides={slides}
                  currentTime={currentTime}
                  duration={duration}
                  isPlaying={isPlaying}
                />
              ) : imageError ? (
                <ImageError onRetry={() => loadTopicImages(currentChapterIndex, currentTopicIndex)} />
              ) : null}

              {/* Progress Bar */}
              {(isPlaying || currentTime > 0) && (
                <div className="mb-4">
                  <div className="h-2 bg-teal-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-500 transition-all duration-200"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{Math.floor(currentTime)}s</span>
                    <span>{Math.floor(duration)}s</span>
                  </div>
                </div>
              )}

              {/* Audio Controls */}
              <div className="flex justify-center items-center gap-4">
                <Button
                  onClick={handlePrevious}
                  disabled={currentChapterIndex === 0 && currentTopicIndex === 0 || isGenerating}
                  variant="outline"
                  size="lg"
                  className="border-teal-200 text-teal-700 hover:bg-teal-50"
                  data-testid="previous-button"
                >
                  <SkipBack className="w-5 h-5" />
                </Button>

                <Button
                  onClick={handlePlayPause}
                  disabled={!audioEnabled || isGenerating}
                  size="lg"
                  className="px-8 min-w-[140px] bg-teal-600 hover:bg-teal-700"
                  data-testid="play-pause-button"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : isPlaying ? (
                    <>
                      <Pause className="w-6 h-6 mr-2" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="w-6 h-6 mr-2" />
                      Play
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleNext}
                  disabled={
                    (currentChapterIndex === syllabus?.chapters.length - 1 &&
                    currentTopicIndex === currentChapter?.topics.length - 1) || isGenerating
                  }
                  variant="outline"
                  size="lg"
                  className="border-teal-200 text-teal-700 hover:bg-teal-50"
                  data-testid="next-button"
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
              </div>

              <div className="text-center mt-4 text-sm text-teal-600">
                Topic {currentTopicIndex + 1} of {currentChapter?.topics.length} |
                Chapter {currentChapterIndex + 1} of {syllabus?.chapters.length}
              </div>
            </div>
          </div>
        )}

        {/* Chapter List */}
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-teal-600" />
            <p className="text-teal-700">Loading syllabus...</p>
          </div>
        ) : syllabus ? (
          <div className="grid grid-cols-1 gap-4">
            {syllabus.chapters.map((chapter, chapterIdx) => (
              <div
                key={chapter.id}
                className={`bg-white rounded-xl shadow-md overflow-hidden border ${
                  chapterIdx === currentChapterIndex ? 'border-2 border-teal-500' : 'border-teal-100'
                }`}
              >
                <div className="p-5 flex justify-between items-center border-b border-teal-100">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Chapter {chapter.order}: {chapter.name}
                  </h3>
                  <Badge className={
                    chapter.term === 'Quarterly' ? 'bg-green-100 text-green-700' :
                    chapter.term === 'Half-yearly' ? 'bg-blue-100 text-blue-700' :
                    'bg-orange-100 text-orange-700'
                  }>{chapter.term}</Badge>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {chapter.topics.map((topic, topicIdx) => (
                      <div
                        key={topic.id}
                        onClick={() => handleChapterClick(chapterIdx, topicIdx)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          chapterIdx === currentChapterIndex && topicIdx === currentTopicIndex
                            ? 'bg-teal-100 border-2 border-teal-500 shadow-sm'
                            : 'bg-gray-50 hover:bg-teal-50 border border-gray-100'
                        }`}
                        data-testid={`topic-item-${chapter.id}-${topic.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{topic.name}</span>
                          <Badge
                            className={topic.status === 'covered'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                            }
                          >
                            {topic.status === 'covered' ? '\u2713' : '\u25CB'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">No syllabus found</div>
        )}

        {/* Info Card */}
        <div className="mt-6 bg-teal-50 rounded-xl border border-teal-100 p-6">
          <h3 className="font-bold mb-2 text-teal-800">How to Use Audio Learning:</h3>
          <ul className="space-y-2 text-sm text-teal-700">
            <li>- Select your preferred language and speaking pace</li>
            <li>- Choose a voice style that suits your learning preference</li>
            <li>- Click Play to start listening to topic summaries</li>
            <li>- AI-generated illustrations appear automatically for each topic</li>
            <li>- Images advance in sync with the audio explanation</li>
            <li>- Use the arrows or dots to manually browse illustrations</li>
            <li>- Audio automatically advances to the next topic</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AudioLearning;
